/**
 * HTTP Client for SDK
 *
 * IMPORTANT: No credentials! SDK runs on external domains,
 * and CORS with wildcard origin doesn't allow credentials.
 * All endpoints are public storefront endpoints.
 */

import type { ApiResponse, SDKResponse } from '../types';
import { getConfig, isInitialized } from './config';
import { ApiError, NetworkError, ShoppexError } from './errors';
import { getOrFetch, type CacheOptions } from './cache';
import { reportStorefrontClientError } from './telemetry';
import { resolveBuyerCurrency } from '../utils/requested-currency';

const DEFAULT_TIMEOUT = 10000;
const MAX_RETRIES = 2;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  timeout?: number;
  retries?: number;
  baseUrl?: string;
  headers?: Record<string, string>;
  cache?: (CacheOptions & { key?: string }) | false;
  /**
   * This read prices its payload, so it travels with the buyer currency from
   * `init({ currency })` as `?currency=` (ADR-0066). The API prices the
   * response in that currency or refuses with
   * `errors.storefront.currency_unavailable`; it never silently serves the
   * shop default. Leave unset for reads that carry no prices.
   */
  buyerCurrency?: boolean;
}

interface ParsedResponsePayload {
  data: unknown | null;
  rawText: string | null;
}

interface FailureInfo {
  message: string;
  statusCode?: number;
  isTransport: boolean;
  responseReceived: boolean;
  responseDefinitive: boolean;
  /** The server's `error_code`, when the failure was a named refusal. */
  code?: string;
  errorParams?: Record<string, unknown>;
}

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function appendQueryParam(url: string, key: string, value: string): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
}

/**
 * Re-shape a failed response for a module's own return type without losing
 * the failure contract: `code` / `errorParams` are what callers branch on
 * (`errors.storefront.currency_unavailable`), `status` / `responseReceived` /
 * `responseDefinitive` are what mutation helpers and retries read. A wrapper
 * that rebuilds `{ success: false, message }` by hand silently drops them.
 */
export function forwardFailure<T>(
  response: SDKResponse<unknown>,
  overrides: { message?: string; data?: T } = {},
): SDKResponse<T> {
  const { success: _success, data: _data, ...failure } = response;
  return {
    ...failure,
    success: false,
    message: overrides.message ?? response.message,
    ...(overrides.data !== undefined ? { data: overrides.data } : {}),
  };
}

export async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<SDKResponse<T>> {
  const config = options.baseUrl ? null : getConfig();
  // The audience (locale, buyer currency) travels with EVERY request of an
  // initialized SDK — a baseUrl override (e.g. resolveStoreByDomain) must not
  // silently drop it back to server-side negotiation (Codex P2).
  const audience = config ?? (isInitialized() ? getConfig() : null);
  const {
    method = 'GET',
    body,
    timeout = DEFAULT_TIMEOUT,
    retries,
    baseUrl,
    headers: requestHeaders,
    cache,
    buyerCurrency: pricesInBuyerCurrency = false,
  } = options;
  const retryCount =
    retries ?? (method === 'GET' ? MAX_RETRIES : 0);

  const apiBaseUrl = baseUrl ?? config?.apiBaseUrl ?? '';
  // One place knows the transport: currency is a query parameter, not a
  // header, because the storefront API keys its HTTP cache on the URL. The
  // value follows the same rule as cart quotes and checkout (page URL, then
  // init config), so the catalog is never priced differently from the cart.
  const buyerCurrency = pricesInBuyerCurrency ? resolveBuyerCurrency(audience?.currency) : null;
  const url = buyerCurrency
    ? appendQueryParam(`${apiBaseUrl}${endpoint}`, 'currency', buyerCurrency)
    : `${apiBaseUrl}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...requestHeaders,
  };
  // Explicit request locale (ADR-0055): without it the backend would fall
  // back to Accept-Language, detaching catalog content from the page locale.
  const locale = typeof audience?.locale === 'string' && audience.locale.trim() ? audience.locale.trim() : null;
  if (locale) {
    headers['x-shoppex-locale'] = locale;
  }

  let lastFailure: FailureInfo | null = null;

  const executeRequest = async (): Promise<SDKResponse<T>> => {
    for (let attempt = 0; attempt <= retryCount; attempt++) {
      let responseReceived = false;
      let responseDefinitive = false;
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(url, {
          method,
          headers,
          body: body ? JSON.stringify(body) : undefined,
          signal: controller.signal,
        });
        responseReceived = true;

        clearTimeout(timeoutId);

        const payload = await parseResponsePayload(response);
        if (!response.ok) {
          responseDefinitive = isDefinitiveHttpRefusal(payload.data)
            && response.status >= 400
            && response.status < 500
            && response.status !== 408;
          const fallbackHttpMessage = response.statusText
            ? `HTTP ${response.status}: ${response.statusText}`
            : `HTTP ${response.status}`;
          const message =
            (payload.data && typeof payload.data === 'object' && 'error' in payload.data && typeof payload.data.error === 'string'
              ? payload.data.error
              : null) ??
            (payload.data && typeof payload.data === 'object' && 'message' in payload.data && typeof payload.data.message === 'string'
              ? payload.data.message
              : null) ??
            payload.rawText ??
            fallbackHttpMessage;

          // A named server refusal keeps its name. Everything else stays a
          // NetworkError, exactly as before.
          const named = readErrorCodeFields(payload.data);
          if (named.code) {
            throw new ApiError(message, named.code, response.status, named.errorParams);
          }

          throw new NetworkError(message, response.status);
        }

        if (response.status === 204 && payload.data === null) {
          return {
            success: true,
          };
        }

        if (!payload.data || typeof payload.data !== 'object' || !('status' in payload.data)) {
          throw new NetworkError('Invalid API response', response.status);
        }

        const data = payload.data as ApiResponse<T>;
        const mapped = mapApiResponse(data);
        return mapped.success
          ? mapped
          : {
              ...mapped,
              responseReceived: true,
              responseDefinitive: data.status >= 400 && data.status < 500 && data.status !== 408,
              status: response.status,
            };
      } catch (error) {
        let normalizedError = error instanceof Error ? error : new Error(String(error));

        if (error instanceof DOMException && error.name === 'AbortError') {
          normalizedError = new NetworkError('Request timeout', 408);
        }

        // Read from the base class, not NetworkError: a named ApiError also
        // carries the HTTP status, and losing it would misclassify a 400
        // refusal as a transport failure — retried and reported as an outage.
        const statusCode =
          normalizedError instanceof ShoppexError
            ? normalizedError.statusCode
            : undefined;

        lastFailure = {
          message: normalizedError.message,
          statusCode,
          isTransport: statusCode === undefined || statusCode === 408,
          responseReceived,
          responseDefinitive,
          ...(normalizedError instanceof ApiError
            ? {
                code: normalizedError.code,
                ...(normalizedError.errorParams ? { errorParams: normalizedError.errorParams } : {}),
              }
            : {}),
        };

        // A named 4xx is the server's decision, not a transport hiccup:
        // `ApiError` only exists when the response carried an error code, and
        // a client-error status means the request itself was refused. Repeating
        // it verbatim cannot change the answer — it just multiplies the load
        // and delays the refusal the caller is waiting on by the full backoff
        // schedule. 408 stays retryable (it is a timeout wearing a 4xx) and
        // every 5xx, network failure and unnamed error retries exactly as
        // before.
        const isNamedClientRefusal =
          normalizedError instanceof ApiError
          && statusCode !== undefined
          && statusCode >= 400
          && statusCode < 500
          && statusCode !== 408;

        if (isNamedClientRefusal) {
          break;
        }

        if (attempt < retryCount) {
          await sleep(Math.pow(2, attempt) * 500);
          continue;
        }
      }
    }

    return {
      success: false,
      message: lastFailure?.message ?? 'Unknown error',
      ...(lastFailure ? { responseReceived: lastFailure.responseReceived } : {}),
      ...(lastFailure?.responseDefinitive ? { responseDefinitive: true } : {}),
      ...(lastFailure?.responseReceived && lastFailure.statusCode !== undefined
        ? { status: lastFailure.statusCode }
        : {}),
      ...(lastFailure?.code ? { code: lastFailure.code } : {}),
      ...(lastFailure?.errorParams ? { errorParams: lastFailure.errorParams } : {}),
    };
  };

  // Every cached payload is specific to the audience it was fetched for, so
  // the key carries the locale and (for priced reads) the buyer currency. A
  // locale or currency switch reads its own entry rather than the previous
  // audience's payload for up to the TTL.
  const result =
    method === 'GET' && cache && cache.ttl > 0
      ? await getOrFetch(
          `${locale ?? ''}|${buyerCurrency ?? ''}|${cache.key ?? `GET:${url}`}`,
          executeRequest,
          { ttl: cache.ttl, staleWhileRevalidate: cache.staleWhileRevalidate },
          (value) => value.success
        )
      : await executeRequest();

  const failureForTelemetry = lastFailure as FailureInfo | null;

  if (!result.success && failureForTelemetry?.isTransport) {

    await reportStorefrontClientError({
      endpoint,
      method,
      message: result.message ?? failureForTelemetry.message,
      statusCode: failureForTelemetry.statusCode,
      attemptCount: retryCount + 1,
      requestUrl: url,
      responseReceived: failureForTelemetry.responseReceived,
    });
  }

  return result;
}

async function parseResponsePayload(response: Response): Promise<ParsedResponsePayload> {
  const responseWithOptionalMethods = response as Response & {
    text?: () => Promise<string>;
    json?: () => Promise<unknown>;
  };

  // Runtime-safe fallback for test mocks that only implement `json()`.
  if (typeof responseWithOptionalMethods.text !== 'function') {
    if (typeof responseWithOptionalMethods.json === 'function') {
      try {
        return {
          data: await responseWithOptionalMethods.json(),
          rawText: null,
        };
      } catch {
        return { data: null, rawText: null };
      }
    }
    return { data: null, rawText: null };
  }

  try {
    const rawText = await responseWithOptionalMethods.text();
    if (!rawText) {
      return { data: null, rawText: null };
    }

    try {
      return {
        data: JSON.parse(rawText) as unknown,
        rawText: null,
      };
    } catch {
      const normalizedText = rawText.trim();
      return {
        data: null,
        rawText: normalizedText.length > 0 ? normalizedText : null,
      };
    }
  } catch {
    return { data: null, rawText: null };
  }
}

function isDefinitiveHttpRefusal(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return false;
  }

  const record = payload as Record<string, unknown>;
  return typeof record.status === 'number' && record.status >= 400 && record.status < 500;
}

function mapApiResponse<T>(apiResponse: ApiResponse<T>): SDKResponse<T> {
  if (apiResponse.status >= 200 && apiResponse.status < 300) {
    return {
      success: true,
      data: apiResponse.data,
      ...(apiResponse.message ? { message: apiResponse.message } : {}),
    };
  }

  return {
    success: false,
    message: apiResponse.error ?? apiResponse.message ?? `Request failed with status ${apiResponse.status}`,
    // The refusal the server actually made, kept machine-readable. Without it
    // a caller can only string-match a localized sentence, which breaks in
    // every locale but one.
    ...readErrorCodeFields(apiResponse),
  };
}

/**
 * Pull the localized-error envelope (`error_code` / `error_params`) off any
 * server payload shape. Returns an empty object when the payload carries none,
 * so the fields stay absent rather than becoming `undefined` keys.
 */
function readErrorCodeFields(payload: unknown): { code?: string; errorParams?: Record<string, unknown> } {
  if (!payload || typeof payload !== 'object') {
    return {};
  }

  const record = payload as Record<string, unknown>;
  const code = typeof record.error_code === 'string' && record.error_code.length > 0
    ? record.error_code
    : null;
  if (!code) {
    return {};
  }

  const params = record.error_params;
  return {
    code,
    ...(params && typeof params === 'object' && !Array.isArray(params)
      ? { errorParams: params as Record<string, unknown> }
      : {}),
  };
}

export async function get<T>(
  endpoint: string,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<SDKResponse<T>> {
  return request<T>(endpoint, { ...options, method: 'GET' });
}

export async function post<T>(
  endpoint: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<SDKResponse<T>> {
  return request<T>(endpoint, { ...options, method: 'POST', body });
}
